/**
 * VisitorPanel — the one place a visitor is asked to sign in (plan `2026_11_02_ovid_visitor_open.md` § Decided 4).
 * The proof's muse section (its title and line, its visitor rows, each choosable) beside the shared sign-in card. It
 * stands at the seam, at the wall's end, and in the layer a tap, the quick look's Render or the header opens: the same
 * component, so the choice made in one is the choice the others show (`chosen` is the route's, not the panel's).
 *
 * The muse of the clicked picture, when the page knows it (`first`), leads the rows and is chosen. Otherwise none is
 * chosen and the card does not wait for one. The choice rides the address the sign-in returns to (`musePanel.ts`).
 */
import {useSearchParams} from 'react-router'
import {routerPaths} from '@routes'
import {SignInCard} from '@core/auth-ui/SignInCard/SignInCard'
import {chosenLine, withMuseChoice} from './musePanel'
import {OVID_SIGN_IN_CARD} from './signInCard'
import type {PanelMuse, VisitorPanelData} from './visitorPanel.server'
import styles from './VisitorPanel.module.scss'

export type VisitorPanelProps = {
    data: VisitorPanelData
    /** The muse key chosen so far, or null. */
    chosen: string | null
    onChoose: (slug: string | null) => void
    /** Where the sign-in returns to, before the choice is added. */
    redirectTo: string
    trigger: string
    /** The card's email field id: several panels can be in one document. */
    inputId: string
    /** The muse of the clicked picture (`museSlug` on its row), when known. */
    first?: string | null
    /** The layer's close; absent for a panel set in the wall's own flow, which carries no back link. */
    onClose?: () => void
}

/** The rows with the clicked picture's muse first, when it is one of them. */
export function orderedMuses(muses: readonly PanelMuse[], first: string | null | undefined): PanelMuse[] {
    const lead = muses.find((muse) => muse.slug === first)
    return lead ? [lead, ...muses.filter((muse) => muse !== lead)] : [...muses]
}

export function VisitorPanel({data, chosen, onChoose, redirectTo, trigger, inputId, first = null, onClose}: VisitorPanelProps) {
    const [searchParams] = useSearchParams()
    const muses = orderedMuses(data.muses, first)
    const current = muses.find((muse) => muse.slug === chosen) ?? null
    return (
        <section className={styles.panel} data-visitor-panel>
            <div className={styles.museSection}>
                <h2 className={styles.title}>{data.title}</h2>
                <p className={styles.line}>{data.line}</p>
                <div className={styles.rows} role="radiogroup" aria-label={data.title}>
                    {muses.map((muse) => {
                        const on = muse.slug === current?.slug
                        return (
                            <button
                                key={muse.slug}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                className={on ? `${styles.row} ${styles.rowOn}` : styles.row}
                                onClick={() => onChoose(muse.slug)}
                            >
                                <span className={styles.scenes}>
                                    {muse.pictures.map((src) => (
                                        <img key={src} src={src} alt="" loading="lazy" className={styles.scene} />
                                    ))}
                                </span>
                                <span className={styles.name}>{muse.name}</span>
                            </button>
                        )
                    })}
                </div>
                {current && <p className={styles.ember}>{chosenLine(current.name)}</p>}
            </div>
            <div className={styles.cardSection}>
                <SignInCard
                    {...OVID_SIGN_IN_CARD}
                    googleAuthAction={routerPaths['/auth/:provider']({provider: 'google'})}
                    authError={searchParams.get('auth-error')}
                    redirectTo={withMuseChoice(redirectTo, current?.slug ?? null)}
                    gateTrigger={trigger}
                    inputId={inputId}
                    onBack={onClose ?? null}
                />
            </div>
        </section>
    )
}

/** The layer a tap, the quick look's Render or the header opens: the scrim, and the panel in a box that scrolls. */
export function VisitorSignInLayer({onClose, ...panel}: VisitorPanelProps & {onClose: () => void}) {
    return (
        <div className={styles.overlay} data-wall-return-exempt="true">
            <div className={styles.scrim} onClick={onClose} />
            <div className={styles.box}>
                <VisitorPanel {...panel} onClose={onClose} />
            </div>
        </div>
    )
}
